export interface PrivacySection {
  title: string;
  content?: string;
  items?: string[];
  subsections?: PrivacySection[];
  contactInfo?: {
    email: string;
    website: string;
  };
}

export interface PrivacyPolicyData {
  title: string;
  lastUpdated: string;
  introduction: string;
  sections: PrivacySection[];
}

export const privacyPolicyEn: PrivacyPolicyData = {
  title: "Privacy Policy – NCircle",
  lastUpdated: "October 20, 2025",
  introduction: "NCircle (\"we,\" \"our,\" \"us\") is a digital platform developed and operated by DeepMinds. We are committed to protecting your privacy and ensuring transparency in how we handle your data. This Privacy Policy explains how we collect, use, and safeguard information when you use the NCircle mobile application, website, and digital services (collectively, the 'Platform'). By using NCircle, you agree to this Privacy Policy. If you do not agree, please discontinue using the Platform.",
  sections: [
    {
      title: "1. Information We Collect",
      content: "When you use NCircle, we may collect the following types of information:",
      subsections: [
        {
          title: "a. Account Information",
          items: [
            "Name, email address, and phone number.",
            "Age verification and identity details (where required for KYC).",
            "Login credentials or third-party login data (e.g., Apple ID, Google)."
          ]
        },
        {
          title: "b. Activity and Wallet Data",
          items: [
            "Participation in verified initiatives (e.g., recycling, walking, or community programs).",
            "Earned and redeemed NCircle Coins (\"NCoins\") and Points.",
            "Transaction details, redemption history, and wallet balance."
          ]
        },
        {
          title: "c. Partner and Enabler Data",
          items: [
            "Business name, contact details, and operational data related to verified initiatives.",
            "Campaign performance and participation metrics (used strictly for reporting)."
          ]
        },
        {
          title: "d. Technical Information",
          items: [
            "Device type, operating system, and app version.",
            "IP address and location (for fraud prevention and initiative verification).",
            "Log files, diagnostic data, and usage analytics."
          ]
        }
      ]
    },
    {
      title: "2. How We Use Your Information",
      items: [
        "Operate, verify, and manage your NCircle account and wallet.",
        "Track participation in verified initiatives and allocate rewards (NCoins and Points).",
        "Facilitate redemption and engagement with approved Partners.",
        "Maintain and improve the security, performance, and usability of the Platform.",
        "Communicate updates, offers, and relevant program information.",
        "Comply with Saudi financial, KYC, and data protection regulations."
      ]
    },
    {
      title: "3. Data Sharing and Disclosure",
      content: "We do not sell, rent, or trade your personal information. Data may only be shared under the following conditions:",
      items: [
        "Service Providers: With trusted third parties that support our operations (e.g., payment gateways, verification APIs, analytics, or hosting).",
        "Regulatory Compliance: When required by law, regulation, or a competent authority under Saudi law.",
        "Partners and Enablers: Only for operational and reporting purposes related to verified initiatives.",
        "Business Transfers: If NCircle is involved in a merger, acquisition, or transfer, your information may be transferred to the new entity.",
        "Aggregated and Anonymized Data: Used for reporting, impact measurement, or service improvement, with no personally identifiable information."
      ]
    },
    {
      title: "4. Data Storage and Security",
      content: "All personal and transactional data is stored securely using industry-standard encryption and access controls. Access is limited to authorized DeepMinds personnel and service providers bound by confidentiality agreements. While we take reasonable measures to protect your data, no system is entirely secure. You acknowledge that information transmitted through the internet may carry inherent risks beyond NCircle's control."
    },
    {
      title: "5. International Data Transfers",
      content: "Your data may be stored or processed in data centers outside Saudi Arabia, depending on our cloud service providers. In all cases, NCircle ensures compliance with Saudi PDPL and equivalent international standards to maintain adequate data protection."
    },
    {
      title: "6. Data Retention",
      content: "We retain personal information only as long as necessary to provide our services and maintain your account, comply with financial and legal obligations, or resolve disputes and enforce our rights. When no longer required, data will be securely deleted, anonymized, or archived according to PDPL guidelines."
    },
    {
      title: "7. Your Rights",
      content: "Under applicable laws, you may have the right to access and obtain a copy of your personal data, request correction or deletion of inaccurate or unnecessary data, withdraw consent where processing is based on consent, and object to or restrict specific types of processing. To exercise these rights, please contact us at info@ncircle.ai. We will respond within a reasonable timeframe in accordance with Saudi PDPL."
    },
    {
      title: "8. Children's Privacy",
      content: "NCircle is not directed to children under 16 years old. We do not knowingly collect personal data from minors without verified parental or guardian consent. If we discover such data was collected without authorization, we will delete it promptly."
    },
    {
      title: "9. Camera Usage",
      content: "NCircle may request access to your device camera to verify initiatives, scan QR codes, or capture photos as part of your participation in the Platform. The camera is used solely for these purposes and will not be accessed without your consent. You can disable camera access in your device settings, but some features of the Platform may not function fully without it."
    },
    {
      title: "10. Explicit Legal Bases for Processing",
      content: "We process your personal data based on consent (where you have provided explicit consent for specific services), contractual necessity (to provide the Platform, manage your account, and fulfill transactions), legitimate interest (to improve services, prevent fraud, and maintain security), and legal obligation (to comply with Saudi law or regulatory requirements). Where applicable, we ensure that data processing is limited to what is necessary and respects your privacy rights."
    },
    {
      title: "11. Push Notifications",
      content: "NCircle may send push notifications to inform you about verified initiatives, rewards or updates. You can opt-out of push notifications at any time in your device or app settings."
    },
    {
      title: "12. Policy Accessibility",
      content: "This Privacy Policy is available within the NCircle app and on our website. Users can access, download, and review the policy at any time. Significant updates will be communicated via in-app notifications or email when appropriate."
    },
    {
      title: "13. Changes to This Policy",
      content: "We may update this Privacy Policy periodically to reflect changes in regulations, technology, or our services. Any updates will be published in the NCircle app or on our website. Continued use of NCircle after such updates means you accept the revised Privacy Policy."
    },
    {
      title: "14. Contact Us",
      content: "If you have questions, concerns, or complaints regarding this Privacy Policy or your personal data, please contact us at:",
      contactInfo: {
        email: "info@ncircle.ai",
        website: "www.ncircle.ai"
      }
    }
  ]
};

export const privacyPolicyAr: PrivacyPolicyData = {
  title: "سياسة الخصوصية – NCircle",
  lastUpdated: "٢٠ أكتوبر ٢٠٢٥",
  introduction: "تعد منصة NCircle (\"نحن\" أو \"الخاصة بنا\" أو \"إننا\") منصة رقمية تم تطويرها وتشغيلها بواسطة DeepMinds. نحن ملتزمون بحماية خصوصيتك وضمان الشفافية في كيفية التعامل مع بياناتك. توضح سياسة الخصوصية هذه كيفية جمع واستخدام وحماية المعلومات عند استخدام تطبيق NCircle للهواتف الذكية وموقعنا الإلكتروني والخدمات الرقمية المرتبطة بهما (يشار إليها مجتمعة بـ \"المنصة\"). باستخدامك لمنصة NCircle، فإنك توافق على سياسة الخصوصية هذه. إذا كنت لا توافق، يُرجى التوقف عن استخدام المنصة.",
  sections: [
    {
      title: "١. المعلومات التي نجمعها",
      content: "عند استخدامك لمنصة NCircle، قد نقوم بجمع الأنواع التالية من المعلومات:",
      subsections: [
        {
          title: "أ. معلومات الحساب",
          items: [
            "الاسم، عنوان البريد الإلكتروني، ورقم الهاتف.",
            "التحقق من العمر وبيانات الهوية (عند الحاجة لإجراءات اعرف عميلك KYC).",
            "بيانات تسجيل الدخول أو بيانات تسجيل الدخول عبر أطراف ثالثة (مثل Apple ID أو Google)."
          ]
        },
        {
          title: "ب. بيانات النشاط والمحفظة",
          items: [
            "المشاركة في المبادرات المعتمدة (مثل إعادة التدوير، المشي، أو البرامج المجتمعية).",
            "نقاط وعملات NCircle المكتسبة والمستردة (\"NCoins\").",
            "تفاصيل المعاملات، وسجل الاسترداد، ورصيد المحفظة."
          ]
        },
        {
          title: "ج. بيانات الشركاء والممكّنين",
          items: [
            "اسم المنشأة، بيانات التواصل، والبيانات التشغيلية المتعلقة بالمبادرات المعتمدة.",
            "أداء الحملات ومؤشرات المشاركة (المستخدمة لأغراض إعداد التقارير فقط)."
          ]
        },
        {
          title: "د. المعلومات التقنية",
          items: [
            "نوع الجهاز، نظام التشغيل، وإصدار التطبيق.",
            "عنوان IP والموقع الجغرافي (لمنع الاحتيال والتحقق من صحة المشاركة في المبادرات).",
            "ملفات السجل، والبيانات التشخيصية، وتحليلات الاستخدام."
          ]
        }
      ]
    },
    {
      title: "٢. كيف نستخدم معلوماتك",
      items: [
        "تشغيل وإدارة والتحقق من حسابك ومحفظتك على منصة NCircle.",
        "تتبع المشاركة في المبادرات المعتمدة وتوزيع المكافآت (NCoins والنقاط).",
        "تسهيل عمليات الاسترداد والتفاعل مع الشركاء المعتمدين.",
        "الحفاظ على أمان وأداء المنصة وتحسين تجربة الاستخدام.",
        "إرسال التحديثات والعروض والمعلومات المتعلقة بالبرامج.",
        "الامتثال للأنظمة المالية السعودية، وإجراءات KYC، وأنظمة حماية البيانات الشخصية (PDPL)."
      ]
    },
    {
      title: "٣. مشاركة البيانات والإفصاح عنها",
      content: "نحن لا نبيع أو نؤجر أو نتاجر بمعلوماتك الشخصية. قد تتم مشاركة البيانات فقط في الحالات التالية:",
      items: [
        "مقدمو الخدمات: مع أطراف ثالثة موثوقة تدعم عملياتنا (مثل بوابات الدفع، وواجهات برمجة التحقق، وخدمات التحليلات، أو الاستضافة).",
        "الامتثال التنظيمي: عند الالتزام بنظام أو لائحة أو بطلب من جهة حكومية مختصة بموجب الأنظمة السعودية.",
        "الشركاء والممكّنون: لأغراض تشغيلية وتقارير تتعلق بالمبادرات المعتمدة فقط.",
        "نقل الملكية والأعمال: في حال اندماج NCircle أو الاستحواذ عليها، قد يتم نقل معلوماتك إلى الكيان الجديد.",
        "البيانات المجمعة ومجهولة الهوية: تُستخدم لقياس الأثر وتحسين الخدمات، دون احتوائها على أي بيانات هوية شخصية."
      ]
    },
    {
      title: "٤. تخزين البيانات وأمنها",
      content: "يتم تخزين كافة البيانات الشخصية والمعاملات بأمان باستخدام أعلى معايير التشفير وضوابط الوصول المعترف بها في هذا المجال. ويقتصر الوصول على الموظفين ومقدمي الخدمات المصرح لهم في DeepMinds بموجب اتفاقيات سرية صارمة. ورغم اتخاذنا لكافة التدابير المعقولة لحماية بياناتك، فإن أي نظام عبر الإنترنت لا يُعد آمنًا بنسبة 100%، وتقر بأن نقل المعلومات عبر الإنترنت قد ينطوي على مخاطر خارجة عن إرادة NCircle."
    },
    {
      title: "٥. نقل البيانات دولياً",
      content: "قد يتم تخزين بياناتك أو معالجتها في مراكز بيانات خارج المملكة العربية السعودية وفقاً لمزودي الخدمات السحابية. وفي جميع الأحوال، تلتزم NCircle بنظام حماية البيانات الشخصية السعودي (PDPL) والمعايير الدولية لضمان الحماية الكافية للبيانات."
    },
    {
      title: "٦. الاحتفاظ بالبيانات",
      content: "نحتفظ بالمعلومات الشخصية فقط للمدة اللازمة لتقديم خدماتنا والحفاظ على حسابك، أو الامتثال للالتزامات المالية والقانونية، أو تسوية النزاعات. وعند انتهاء الحاجة إليها، سيتم حذف البيانات أو إخفاء هويتها أو أرشفتها بشكل آمن وفق ضوابط نظام حماية البيانات الشخصية."
    },
    {
      title: "٧. حقوقك",
      content: "بموجب الأنظمة السارية، يحق لك الوصول إلى نسخة من بياناتك الشخصية، أو طلب تصحيح البيانات غير الدقيقة، أو حذفها عند عدم الحاجة إليها، أو سحب الموافقة على المعالجة في أي وقت، والاعتراض على معالجات محددة. لممارسة هذه الحقوق، يُرجى مراسلتنا عبر info@ncircle.ai. وسنرد في غضون فترة معقولة ووفقاً لنظام حماية البيانات الشخصية السعودي."
    },
    {
      title: "٨. خصوصية الأطفال",
      content: "منصة NCircle غير موجهة للأطفال دون سن 16 عامًا. نحن لا نجمع عن علم أي بيانات شخصية من القاصرين دون موافقة موثقة من ولي الأمر أو الوصي. وإذا اكتشفنا جمع مثل هذه البيانات دون تصريح، فسنقوم بحذفها فورًا."
    },
    {
      title: "٩. استخدام الكاميرا",
      content: "قد تطلب NCircle الوصول إلى كاميرا جهازك للتحقق من المبادرات، أو مسح رموز QR، أو التقاط صور توثيقية كجزء من مشاركتك. تُستخدم الكاميرا حصريًا لهذه الأغراض ولن يتم الوصول إليها دون موافقتك الصريحة. يمكنك إيقاف الوصول إلى الكاميرا من إعدادات جهازك في أي وقت."
    },
    {
      title: "١٠. المسوغات النظامية لمعالجة البيانات",
      content: "نقوم بمعالجة بياناتك الشخصية بناءً على الموافقة الصريحة، أو الضرورة التعاقدية (لتشغيل المنصة وإدارة حسابك وإتمام المعاملات)، أو المصلحة المشروعة (لتحسين الخدمات ومنع الاحتيال وضمان الأمن)، أو الالتزام القانوني بموجب الأنظمة السعودية. ونحرص دائماً على أن تقتصر المعالجة على ما هو ضروري ويحترم حقوقك."
    },
    {
      title: "١١. الإشعارات الفورية",
      content: "قد ترسل NCircle إشعارات فورية لتنبيهك بشأن المبادرات والمكافآت والتحديثات. يمكنك تعطيل هذه الإشعارات في أي وقت عبر إعدادات تطبيقك أو جهازك."
    },
    {
      title: "١٢. إتاحة السياسة وسهولة الوصول",
      content: "تتوفر سياسة الخصوصية هذه داخل تطبيق NCircle وعلى موقعنا الإلكتروني، ويمكن للمستخدمين الاطلاع عليها ومراجعتها في أي وقت. كما سيتم إخطار المستخدمين بأي تحديثات جوهرية عبر الإشعارات أو البريد الإلكتروني."
    },
    {
      title: "١٣. التغييرات على هذه السياسة",
      content: "قد نقوم بتحديث سياسة الخصوصية بشكل دوري لمواكبة التغييرات التنظيمية أو التقنية أو في خدماتنا. وسيتم نشر أي تحديث في التطبيق أو على الموقع الإلكتروني. ويعد استمرارك في استخدام المنصة بعد التحديث موافقة على النسخة المعدلة."
    },
    {
      title: "١٤. تواصل معنا",
      content: "إذا كانت لديك أي أسئلة أو استفسارات أو ملاحظات بخصوص سياسة الخصوصية أو بياناتك الشخصية، يُرجى التواصل معنا عبر:",
      contactInfo: {
        email: "info@ncircle.ai",
        website: "www.ncircle.ai"
      }
    }
  ]
};
